/**
 * Analytics GraphQL Queries
 *
 * 7 queries for business intelligence operations:
 * - getAnalytics (single)
 * - listAnalytics (paginated)
 * - getAnalyticsByCategory
 * - getAnalyticsWithThresholdBreaches
 * - getAnalyticsBySourcePrimitive
 * - getAnalyticsDashboard
 * - getTimeSeriesData
 *
 * @see graphql_gateway/src/analytics_api/analytics_graphql.rs - Backend implementation
 */

import { gql } from '@apollo/client';
import {
  ANALYTICS_FIELDS,
  ANALYTICS_CONNECTION_FIELDS,
  ANALYTICS_DASHBOARD_FIELDS,
  TIME_SERIES_DATA_POINT_FIELDS,
} from './fragments';

/** Get analytics by ID */
export const GET_ANALYTICS = gql`
  ${ANALYTICS_FIELDS}
  query GetAnalytics($analyticsId: String!) {
    getAnalytics(analyticsId: $analyticsId) {
      ...AnalyticsFields
    }
  }
`;

/** List analytics for tenant with pagination */
export const LIST_ANALYTICS = gql`
  ${ANALYTICS_CONNECTION_FIELDS}
  query ListAnalytics($tenantId: String!, $limit: Int, $cursor: String) {
    listAnalytics(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
      ...AnalyticsConnectionFields
    }
  }
`;

/** Get analytics by category (Performance, Adoption, Business, System, Compliance) */
export const GET_ANALYTICS_BY_CATEGORY = gql`
  ${ANALYTICS_FIELDS}
  query GetAnalyticsByCategory($tenantId: String!, $category: MetricCategory!) {
    getAnalyticsByCategory(tenantId: $tenantId, category: $category) {
      ...AnalyticsFields
    }
  }
`;

/** Get analytics with threshold breaches (critical alerts) */
export const GET_ANALYTICS_WITH_THRESHOLD_BREACHES = gql`
  ${ANALYTICS_FIELDS}
  query GetAnalyticsWithThresholdBreaches($tenantId: String!) {
    getAnalyticsWithThresholdBreaches(tenantId: $tenantId) {
      ...AnalyticsFields
    }
  }
`;

/** Get analytics by source primitive type (Queue, Actor, SLA, etc.) */
export const GET_ANALYTICS_BY_SOURCE_PRIMITIVE = gql`
  ${ANALYTICS_FIELDS}
  query GetAnalyticsBySourcePrimitive($tenantId: String!, $sourcePrimitiveType: String!) {
    getAnalyticsBySourcePrimitive(tenantId: $tenantId, sourcePrimitiveType: $sourcePrimitiveType) {
      ...AnalyticsFields
    }
  }
`;

/** Get analytics dashboard with all categories */
export const GET_ANALYTICS_DASHBOARD = gql`
  ${ANALYTICS_DASHBOARD_FIELDS}
  query GetAnalyticsDashboard($tenantId: String!) {
    getAnalyticsDashboard(tenantId: $tenantId) {
      ...AnalyticsDashboardFields
    }
  }
`;

/** Get time series data for specific analytics */
export const GET_TIME_SERIES_DATA = gql`
  ${TIME_SERIES_DATA_POINT_FIELDS}
  query GetTimeSeriesData($analyticsId: String!, $startTime: DateTime!, $endTime: DateTime!) {
    getTimeSeriesData(analyticsId: $analyticsId, startTime: $startTime, endTime: $endTime) {
      ...TimeSeriesDataPointFields
    }
  }
`;
